import React, { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import useFormWithSchema from "../../hooks/useFormWithSchema";
import * as Yup from "yup";
import i18n from "../../i18n";
import { Controller } from "react-hook-form";
import { Button } from "../button";
import DateTimePickerInput from "../inputs/DateTimePickerInputOld";
import { useGetVisitingHours } from "../../hooks/common/query";
import { useUpdateVisitingHours } from "../../hooks/common/mutation";
import toast from "react-hot-toast";
import { convertTimeToISOString, formatToTime } from "../../lib/functions";
import Loader from "../loader";
import TextInput from "../inputs/TextInput";

export const VisitorVisitingHoursFRConfigurationsCardSchema = Yup.object({
  visitStartTime: Yup.string().required(
    i18n.language === "en"
      ? "Visit start time is required."
      : "يجب تحديد وقت بدء الزيارة."
  ),
  visitEndTime: Yup.string().required(
    i18n.language === "en"
      ? "Visit end time is required."
      : "يجب تحديد وقت انتهاء الزيارة."
  ),
  allowedVisitDuration: Yup.number().required(
    i18n.language === "en"
      ? "Allowed visit duration is required."
      : "مدة الزيارة المسموح بها مطلوبة."
  ),
});

interface FormData {
  visitStartTime: string;
  visitEndTime: string;
  allowedVisitDuration: number;
}

const VisitorVisitingHoursFRConfigurationsCard = () => {
  const { t } = useTranslation();

  const {
    control,
    formState: { errors },
    handleSubmit,
    clearErrors,
    reset,
  } = useFormWithSchema(VisitorVisitingHoursFRConfigurationsCardSchema);

  const { data: visitingHours, isPending: isVisitingHoursLoading } =
    useGetVisitingHours();

  useEffect(() => {
    if (visitingHours) {
      reset({
        visitStartTime: convertTimeToISOString(visitingHours.visitorsStartTime),
        visitEndTime: convertTimeToISOString(visitingHours.visitorsEndTime),
        allowedVisitDuration: visitingHours.visitorsMaxAllowedDays,
      });
    }
  }, [reset, visitingHours]);

  const {
    mutateAsync: UpdateVisitingHours,
    isPending: isUpdateVisitingHoursLoading,
  } = useUpdateVisitingHours();

  const onSubmit = useCallback(
    async (data: FormData) => {
      try {
        const body = {
          visitorsStartTime: formatToTime(data.visitStartTime),
          visitorsEndTime: formatToTime(data.visitEndTime),
          visitorsMaxAllowedDays: data.allowedVisitDuration,
        };

        await UpdateVisitingHours(body);
        toast.success(t("visitingHoursUpdatedSuccessfully"));
      } catch (error) {
        toast.error(t("somethingWentWrong"));
      }
    },
    [UpdateVisitingHours, t]
  );

  return (
    <div className="flex flex-col gap-2 lg:gap-3">
      <h2 className="text-lg lg:text-xl font-medium">
        {t("visitorVisitingHours")}
      </h2>

      {isVisitingHoursLoading ? (
        <Loader />
      ) : (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="grid sm:grid-cols-3 gap-2 md:gap-4 items-end"
        >
          <Controller
            name="visitStartTime"
            control={control}
            render={({ field: { name, value, onChange } }) => (
              <DateTimePickerInput
                isTimeOnly
                label={t("visitStartTime")}
                value={value}
                required
                onChange={(value) => {
                  onChange(value);
                  clearErrors(name);
                }}
                error={errors?.[name]?.message}
              />
            )}
          />

          <Controller
            name="visitEndTime"
            control={control}
            render={({ field: { name, value, onChange } }) => (
              <DateTimePickerInput
                isTimeOnly
                label={t("visitEndTime")}
                value={value}
                required
                onChange={(value) => {
                  onChange(value);
                  clearErrors(name);
                }}
                error={errors?.[name]?.message}
              />
            )}
          />

          <Controller
            name="allowedVisitDuration"
            control={control}
            render={({ field: { name, value, onChange } }) => (
              <TextInput
                type="number"
                label={t("allowedVisitDuration")}
                value={value?.toString() || ""}
                onChange={(value) => {
                  onChange(value);
                  clearErrors(name);
                }}
                required
                error={errors?.[name]?.message}
              />
            )}
          />

          <Button
            isLoading={isUpdateVisitingHoursLoading}
            className="max-w-max !py-[14px] !px-9"
          >
            {t("update")}
          </Button>
        </form>
      )}
    </div>
  );
};

export default React.memo(VisitorVisitingHoursFRConfigurationsCard);
